import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Layout, Layouts } from 'react-grid-layout';
import { Box, LinearProgress, Typography, Button } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import debounce from 'lodash/debounce';
import clsx from 'clsx';

import { INTERFACE_PERMISSIONS, UserDashboard, UserDashboards, WidgetItem } from '@vyce/core/src/types';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { AppDashboard, getInitialLayouts } from '@vyce/core/src/components/Dashboard/AppDashboard';
import { CustomizeDashboard, ActivateSubscriptionDialog } from '@vyce/core/src/components';
import { DeviceContext } from '@vyce/core/src/contexts';
import { WeatherWidget } from '@vyce/core/src/views/widgets/WeatherWidget';
import { WEATHER_WIDGET_MIN_WIDTHS } from '@vyce/core/src/views/widgets/WeatherWidget/constants';
import { getPaddingForContent } from '@vyce/core/src/utils';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { DayQuoteWidget } from '@vyce/core/src/views/widgets/DayQuoteWidget';

import { useTypedSelector, useInviteButton, useActions } from 'src/hooks';
import { WidgetModule, UniversalInviteModule } from 'src/modules';
import { WelcomeWidget } from './WelcomeWidget';
import { EmployeeListWidget } from './EmployeeListWidget';
import { InviteListWidget } from './InviteListWidget';
import { getFromLS, saveToLS } from '@vyce/core/src/utils/local-storage';

const welcomeWidget: WidgetItem = {
  name: 'Welcome to Vyce!',
  title_color: 'primary',
  type: 'welcome',
  system_name: 'welcome',
  layout: { i: 'welcome', x: 0, y: 0, w: 4, h: 8, minH: 3, minW: 2, isResizable: true },
  is_active: true,
};

const weatherWidget: WidgetItem = {
  name: 'Weather',
  type: 'weather',
  system_name: 'weather',
  layout: { i: 'weather', x: 4, y: 0, w: 4, h: 8, minH: 3, minW: 3, isResizable: true },
  is_active: true,
};

const employeesWidget: WidgetItem = {
  name: 'Team Members',
  type: 'employees',
  system_name: 'employees',
  layout: { i: 'employees', x: 0, y: 8, w: 8, h: 14, minH: 8, minW: 4, isResizable: true },
  is_active: true,
};

const invitesWidget: WidgetItem = {
  name: 'Invites',
  type: 'invites',
  system_name: 'invites',
  layout: { i: 'invites', x: 0, y: 22, w: 8, h: 12, minH: 6, minW: 4, isResizable: true },
  is_active: true,
};

export const DASHBOARD_ITEMS: WidgetItem[] = [welcomeWidget, weatherWidget, employeesWidget, invitesWidget];
// TODO: Find api or create proxy
// const quoteWidget: WidgetItem = {
//   name: 'Quote of the day',
//   type: 'quote',
//   system_name: 'quote',
//   layout: { i: 'quote', x: 0, y: 9, w: 8, h: 8, minH: 3, minW: 3 },
//   is_active: true,
// };

// Legacy localStorage keys — read only to migrate pre-existing settings.
const LS_ITEMS_KEY = 'generalDashboardItems';
const LS_LAYOUT_KEY = 'generalDashboardLayouts';

// PUT /dashboards keeps `is_active` only for widgets registered in the backend
// widget registry — for the General module that is just `welcome`, so visibility
// of the other widgets is stored here per user until the backend registers them.
// Kept on logout (see clearLSExcept in views/main/Main.tsx).
const LS_WIDGETS_STATE_KEY = 'generalDashboardWidgetsState';

const saveWidgetVisibility = (userUuid: string, widgets: WidgetItem[]) => {
  const visibilityByUser = getFromLS(LS_WIDGETS_STATE_KEY) || {};
  visibilityByUser[userUuid] = Object.fromEntries(
    widgets.map(widget => [widget.system_name, !!widget.is_active])
  );
  saveToLS(LS_WIDGETS_STATE_KEY, visibilityByUser);
};

// react-grid-layout reports layouts only for the widgets currently rendered, so
// keep the stored entries of hidden widgets to restore their position on re-open.
const withHiddenWidgetLayouts = (newLayouts: Layouts, prevLayouts?: Layouts): Layouts => {
  if (!prevLayouts) {
    return newLayouts;
  }
  return Object.fromEntries(
    Object.entries(newLayouts).map(([breakpoint, items]) => [
      breakpoint,
      [
        ...(items || []),
        ...(prevLayouts[breakpoint] || []).filter(prevItem => !items?.some(item => item.i === prevItem.i)),
      ],
    ])
  );
};

const withMissingWidgetLayouts = (layouts: Layouts, widgets: WidgetItem[]): Layouts =>
  Object.fromEntries(
    Object.entries(layouts).map(([breakpoint, items]) => [
      breakpoint,
      [
        ...(items || []),
        ...widgets
          .filter(widget => widget.layout && !items?.some(item => item.i === widget.layout?.i))
          .map(widget => widget.layout as Layout),
      ],
    ])
  );

export const EmployerDashboard: React.FC = () => {
  const { isMobile } = useContext(DeviceContext);
  const { first_name, dashboards, uuid } = useTypedSelector(state => state.user);
  const { selectedCompany, selectedCompanyAppData, mainDashboardFilters } = useTypedSelector(
    state => state.helper
  );
  const { updateMainDashboardFilters, updateUserDashboards } = useActions();

  const [generalDashboard, setGeneralDashboard] = useState<UserDashboard>();
  const [isTeamCreated, setIsTeamCreated] = useState<boolean>(true);
  const [isTimeRegistered, setIsTimeRegistered] = useState<boolean>(true);
  const [isPayrollRegistered, setIsPayrollRegistered] = useState<boolean>(false);
  const horizontalScrollClasses = useHorizontalScrollStyles();
  const {
    isActivateSubscriptionDialogOpen,
    isInviteFormOpen,
    subscribeLoading,
    closeActivateSubscriptionDialog,
    setCloseInviteForm,
    handleInviteButtonClick,
    simpleSubscribe,
    canInvite,
  } = useInviteButton();
  const { can, canAny } = usePermissions();
  const { teams, paySchedules, isJobPosted, companyDataLoading, locations } = selectedCompanyAppData || {};
  const padding = getPaddingForContent(isMobile);

  const getDefaultWidgets = (): WidgetItem[] => {
    const isPayrollPermissions = canAny([
      INTERFACE_PERMISSIONS.PAYROLL,
      INTERFACE_PERMISSIONS.PAYROLL_OUTSOURCED,
    ]);
    const isConnectPermissions = can(INTERFACE_PERMISSIONS.CONNECT);
    const isTeamsPermissions = can(INTERFACE_PERMISSIONS.TEAMS);
    const isTimePermissions = can(INTERFACE_PERMISSIONS.TIME);

    // A widget the permissions don't allow is left out of the list entirely,
    // not merely switched off: `is_active` doubles as the user's own show/hide
    // preference and is restored from localStorage below, so a stored `true`
    // would otherwise resurrect a widget after the permission was revoked.
    const anyModulePermission =
      isPayrollPermissions || isConnectPermissions || isTeamsPermissions || isTimePermissions;

    return [
      ...(anyModulePermission ? [welcomeWidget] : []),
      weatherWidget,
      ...(isPayrollPermissions ? [employeesWidget] : []),
      invitesWidget,
    ];
  };

  const getWelcomeData = () => {
    setIsPayrollRegistered(!!selectedCompany?.pay_company);
    setIsTeamCreated(!!teams?.length);
    setIsTimeRegistered(!!locations?.length);

    const defaultWidgets = getDefaultWidgets();
    const apiWidgets = dashboards?.general?.widgets || [];
    const legacyWidgets: WidgetItem[] = getFromLS(LS_ITEMS_KEY) || [];
    const visibilityByUser = getFromLS(LS_WIDGETS_STATE_KEY) || {};
    const userVisibility: Record<string, boolean> | undefined = uuid ? visibilityByUser[uuid] : undefined;

    // Visibility: per-user localStorage state, then the legacy localStorage copy,
    // then the API (only registry-backed widgets survive the PUT round-trip),
    // then the permission-based default.
    const getIsActive = (defaultWidget: WidgetItem): boolean => {
      const systemName = defaultWidget.system_name;
      if (userVisibility && systemName in userVisibility) return userVisibility[systemName];
      const legacyWidget = legacyWidgets.find(widget => widget.system_name === systemName);
      if (legacyWidget) return !!legacyWidget.is_active;
      const apiWidget = apiWidgets.find(widget => widget.system_name === systemName);
      if (apiWidget) return !!apiWidget.is_active;
      return !!defaultWidget.is_active;
    };

    const widgets = defaultWidgets.map(widget => ({ ...widget, is_active: getIsActive(widget) }));

    // Layouts fully survive the API round-trip: prefer them, backfill entries the
    // API copy does not have yet from the legacy localStorage copy, then defaults.
    const apiLayouts = dashboards?.general?.layouts;
    const legacyLayouts: Layouts | null = getFromLS(LS_LAYOUT_KEY);
    const baseLayouts = apiLayouts
      ? withHiddenWidgetLayouts(apiLayouts, legacyLayouts || undefined)
      : legacyLayouts || getInitialLayouts(widgets);
    const layouts = withMissingWidgetLayouts(baseLayouts, widgets);

    setGeneralDashboard({ widgets, layouts });

    if (!userVisibility && uuid && legacyWidgets.length) {
      // One-time migration: seed the per-user visibility state from the legacy
      // keys so old preferences survive the logout localStorage wipe.
      saveWidgetVisibility(uuid, widgets);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      getWelcomeData();
    }
  }, [dashboards?.general, selectedCompany, teams, paySchedules, isJobPosted]);

  const updateDashboards = useCallback(
    (newDashboards: UserDashboards) => {
      updateUserDashboards(newDashboards);
    },
    [updateUserDashboards]
  );

  const handleUpdateDashboards = useMemo(() => debounce(updateDashboards, 500), [updateDashboards]);

  const onWidgetStatusChange = (systemName: string) => {
    const dashboardWidgets = generalDashboard?.widgets || [];
    if (dashboardWidgets.length === 0) return;
    const newWidgets: WidgetItem[] = dashboardWidgets.map(item =>
      item.system_name === systemName ? { ...item, is_active: !item.is_active } : item
    );
    const newSettings = { ...generalDashboard, widgets: newWidgets };
    setGeneralDashboard(newSettings);
    if (uuid) saveWidgetVisibility(uuid, newWidgets);
    handleUpdateDashboards({ ...dashboards, general: newSettings });
  };

  const resetDashboards = () => {
    if (generalDashboard && generalDashboard.widgets) {
      const restoredDashboard = generalDashboard.widgets.map(item => {
        return { ...item, is_active: true };
      });
      const newSettings = { ...generalDashboard, widgets: restoredDashboard };
      setGeneralDashboard(newSettings);
      if (uuid) saveWidgetVisibility(uuid, restoredDashboard);
      updateUserDashboards({ ...dashboards, general: newSettings });
    }
  };

  const getWidget = (widget: WidgetItem) => {
    switch (widget.system_name) {
      case 'welcome':
        return {
          component: WelcomeWidget,
          props: { isJobPosted, isPayrollRegistered, isTeamCreated, isTimeRegistered },
        };
      case 'weather':
        return { component: WeatherWidget, props: {} };
      case 'employees':
        return { component: EmployeeListWidget, props: {} };
      case 'invites':
        return { component: InviteListWidget, props: {} };
      case 'quote':
        return { component: DayQuoteWidget, props: {} };

      default:
        return { component: WidgetModule, props: { widget, mainDashboardFilters } };
    }
  };

  const onLayoutChange = (currentLayout: Layout[], newLayouts: Layouts) => {
    if (!generalDashboard?.widgets?.length) return;
    const newSettings = {
      ...generalDashboard,
      layouts: withHiddenWidgetLayouts(newLayouts, generalDashboard.layouts),
    };
    setGeneralDashboard(newSettings);
    handleUpdateDashboards({ ...dashboards, general: newSettings });
  };

  const onWidgetsChangeViaSettings = (newWidget: WidgetItem[]) => {
    if (!generalDashboard?.widgets?.length) return;
    const newSettings = { ...generalDashboard, widgets: newWidget };
    setGeneralDashboard(newSettings);
    if (uuid) saveWidgetVisibility(uuid, newWidget);
    handleUpdateDashboards({ ...dashboards, general: newSettings });
  };

  if (companyDataLoading) {
    return <LinearProgress />;
  }

  return (
    <Box padding={padding}>
      <Box
        className={clsx(
          horizontalScrollClasses.blockWrapper,
          horizontalScrollClasses.blockWithHideScroll,
          horizontalScrollClasses.navBarBlock
        )}>
        <Box display="flex">
          {!isMobile && (
            <>
              <Typography
                variant="subtitle1"
                style={{ marginRight: 4, fontWeight: 600, whiteSpace: 'nowrap' }}
                color="secondary">
                Hey {first_name},
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>
                here’s your dashboard!
              </Typography>
            </>
          )}
        </Box>

        <Box display="flex" gridGap={16}>
          {canInvite && (
            <Button
              onClick={handleInviteButtonClick}
              size="small"
              data-tour="invite"
              startIcon={<AiOutlinePlus />}
              variant="contained"
              color="primary">
              Invite New Members
            </Button>
          )}
          <CustomizeDashboard
            widgetItems={generalDashboard?.widgets || []}
            companyId={selectedCompany?.uuid ?? ''}
            teamPermission={false}
            timePermission={false}
            mainDashboardFiltersFromState={mainDashboardFilters}
            updateMainDashboardFilters={updateMainDashboardFilters}
            onWidgetsChangeViaSettings={onWidgetsChangeViaSettings}
            resetDashboards={resetDashboards}
          />
        </Box>
      </Box>

      <Box style={{ marginRight: '-24px', marginLeft: '-24px' }}>
        <AppDashboard
          layouts={generalDashboard?.layouts}
          onLayoutChange={onLayoutChange}
          widgets={generalDashboard?.widgets || []}
          getWidget={getWidget}
          onWidgetStatusChange={onWidgetStatusChange}
          widgetMinWidths={WEATHER_WIDGET_MIN_WIDTHS}
        />
      </Box>

      <UniversalInviteModule isOpen={isInviteFormOpen} onClose={setCloseInviteForm} />

      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={simpleSubscribe}
        loading={subscribeLoading}
        wasSubscribed={selectedCompany?.subscribed_company}
      />
    </Box>
  );
};
